import React, { useCallback, useEffect, useRef, useState } from 'react';
import { videoSources } from '../utils/videoSources';
import {
  getCachedBlobUrlSync,
  getCachedMedia,
  fetchAndCacheMedia,
  isCacheableUrl,
  saveMediaToDevice,
} from '../utils/mediaCache';
import { triggerHaptic } from '../utils/nativeBridge';

// Threads-style feed video: autoplays muted while mostly visible, pauses when
// scrolled away, tap opens fullscreen with sound, corner button toggles mute.

// Only one feed video plays at a time
let activeVideo = null;
// Unmuting one video carries over to the next ones this session (like Instagram/Threads)
let preferMuted = true;

const VISIBLE_RATIO = 0.6;

const formatRemaining = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '';
  const s = Math.ceil(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const isFullscreen = (video) =>
  document.fullscreenElement === video ||
  document.webkitFullscreenElement === video ||
  video?.webkitDisplayingFullscreen;

// `url` is the stored media URL; videoSources picks AV1/H.264 per device
const FeedVideo = ({
  url,
  className = '',
  fill = false,
  maxHeightClass = 'max-h-[520px]',
  canSave = false,
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const mutedBeforeFullscreenRef = useRef(true);
  const [muted, setMuted] = useState(preferMuted);
  const [remaining, setRemaining] = useState(null);
  const [inView, setInView] = useState(false);

  // Check persistent on-device media storage for instant 0ms playback
  const syncBlobUrl = isCacheableUrl(url) ? getCachedBlobUrlSync(url) : null;
  const [cachedSource, setCachedSource] = useState(syncBlobUrl);

  useEffect(() => {
    if (!url || !isCacheableUrl(url)) {
      setCachedSource(null);
      return;
    }

    const immediate = getCachedBlobUrlSync(url);
    if (immediate) {
      setCachedSource(immediate);
      return;
    }

    let cancelled = false;
    getCachedMedia(url).then((cached) => {
      if (!cancelled && cached?.blobUrl) {
        setCachedSource(cached.blobUrl);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [url]);

  // Play while mostly visible, pause otherwise
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO),
      { threshold: [0, VISIBLE_RATIO, 1] }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || isFullscreen(video)) return;
    if (inView) {
      if (activeVideo && activeVideo !== video && !isFullscreen(activeVideo)) activeVideo.pause();
      activeVideo = video;
      video.muted = muted;
      video.play().catch(() => {
        // Autoplay with sound can be blocked; fall back to muted
        video.muted = true;
        setMuted(true);
        video.play().catch(() => {});
      });

      // Cache video to device storage in background when viewed if not yet cached
      if (url && isCacheableUrl(url) && !cachedSource) {
        fetchAndCacheMedia(url)
          .then((res) => {
            if (res?.blobUrl) setCachedSource(res.blobUrl);
          })
          .catch(() => {});
      }
    } else {
      video.pause();
      if (activeVideo === video) activeVideo = null;
    }
  }, [inView]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    if (activeVideo === videoRef.current) activeVideo = null;
  }, []);

  // Leaving fullscreen: back to inline playback with the previous mute state
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const onExit = () => {
      if (isFullscreen(video)) return;
      video.controls = false;
      video.muted = mutedBeforeFullscreenRef.current;
      setMuted(video.muted);
      if (inView) video.play().catch(() => {});
    };
    video.addEventListener('webkitendfullscreen', onExit); // iOS
    document.addEventListener('fullscreenchange', onExit);
    document.addEventListener('webkitfullscreenchange', onExit);
    return () => {
      video.removeEventListener('webkitendfullscreen', onExit);
      document.removeEventListener('fullscreenchange', onExit);
      document.removeEventListener('webkitfullscreenchange', onExit);
    };
  }, [inView]);

  const openFullscreen = useCallback((e) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    mutedBeforeFullscreenRef.current = video.muted;
    video.muted = false;
    video.controls = true;
    video.play().catch(() => {});
    if (video.webkitEnterFullscreen) {
      video.webkitEnterFullscreen(); // iOS (incl. WKWebView)
    } else if (video.requestFullscreen) {
      video.requestFullscreen().catch(() => {});
    } else if (video.webkitRequestFullscreen) {
      video.webkitRequestFullscreen();
    }
  }, []);

  const toggleMute = useCallback((e) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    preferMuted = video.muted;
    setMuted(video.muted);
    if (video.paused) video.play().catch(() => {});
  }, []);

  return (
    <div
      ref={containerRef}
      className={`relative ${fill ? 'w-full h-full' : ''} ${className}`}
      onClick={openFullscreen}
      role="button"
      aria-label="Play video fullscreen"
    >
      <video
        key={cachedSource || url}
        ref={videoRef}
        preload="metadata"
        muted={muted}
        loop
        playsInline
        onTimeUpdate={(e) => setRemaining(e.currentTarget.duration - e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setRemaining(e.currentTarget.duration)}
        className={`block w-full ${fill ? 'h-full' : maxHeightClass} object-cover bg-black`}
      >
        {cachedSource ? (
          <source src={cachedSource} type="video/mp4" />
        ) : (
          videoSources(url, '#t=0.1')
        )}
      </video>

      {remaining != null && formatRemaining(remaining) && (
        <span className="absolute left-2.5 bottom-2.5 px-1.5 py-0.5 rounded-md bg-black/55 text-white text-[11px] font-medium tabular-nums pointer-events-none">
          {formatRemaining(remaining)}
        </span>
      )}

      {canSave && (
        <button
          type="button"
          onClick={async (e) => {
            e.stopPropagation();
            triggerHaptic('medium');
            await saveMediaToDevice(url);
          }}
          aria-label="Save video to device"
          className="absolute left-2.5 top-2.5 w-8 h-8 rounded-full bg-black/55 hover:bg-black/80 text-white flex items-center justify-center active:scale-90 transition-transform shadow-md"
          title="Save video to device"
        >
          <i className="fas fa-arrow-down-to-bracket text-[12px]" />
        </button>
      )}

      <button
        type="button"
        onClick={toggleMute}
        aria-label={muted ? 'Unmute' : 'Mute'}
        className="absolute right-2.5 bottom-2.5 w-8 h-8 rounded-full bg-black/55 text-white flex items-center justify-center active:scale-90 transition-transform"
      >
        <i className={`fas ${muted ? 'fa-volume-xmark' : 'fa-volume-high'} text-[13px]`} />
      </button>
    </div>
  );
};

export default FeedVideo;
